<template>
  <minimal-layout>
    <!-- Hidden PrimeVue component to ensure styles load immediately -->
    <Button class="hidden" />
    <div id="dropdown" />
    <div v-if="$page.props.auth.user.owner" class="flex flex-col">
      <div class="md:flex md:h-screen md:flex-col">
        <div class="flex grow overflow-hidden">
          <main-menu-admin
            class="w-56 shrink-0 overflow-y-auto bg-indigo-800 p-12 flex flex-col"
          />
          <div
            class="px-4 py-4 md:flex-1 md:overflow-y-auto md:p-8"
            scroll-region
          >
            <flash-messages />
            <slot />
          </div>
        </div>
      </div>
    </div>
    <div
      v-else
      class="flex h-screen flex-col overflow-y-hidden"
      :class="{ 'overflow-hidden': showMenu }"
    >
      <div class="md:flex" :class="showMenu ? 'z-20 relative' : ''">
        <div
          class="flex items-center bg-white pl-6 pr-0 py-1 md:shrink-0 z-30 border-b-1 md:shadow-none md:border-b w-full relative"
        >
          <!-- Left: logo -->
          <Link class="mt-1 shrink-0" :href="$routes.root()" aria-label="Home">
            <logo class="h-10 sm:h-18 w-10 sm:w-28" />
          </Link>

          <!-- Center: LiveCert -->
          <div class="flex grow justify-center">
            <a
              class="font-[Calibri] text-primary-color ml-1 text-sm xs:text-xl md:text-2xl whitespace-nowrap"
              href="https://arcadia.ro/live_cert.html"
              target="_blank"
            >
              LiveCert@arcadia.ro
            </a>
          </div>

          <!-- Right: mobile actions -->
          <div class="flex md:hidden items-center gap-1 shrink-0 pr-2">
            <notifications-popup icon-class="text-black" />
            <button
              class="flex items-center text-primary-color font-semibold cursor-pointer ml-3"
              @click="toogleMenu()"
            >
              <svg
                class="mr-1 h-6 w-6 fill-primary-color"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
              >
                <path d="M0 3h20v2H0V3zm0 6h20v2H0V9zm0 6h20v2H0v-2z" />
              </svg>
            </button>
          </div>

          <!-- Right: desktop menu, notifications, user dropdown -->
          <div
            class="hidden md:flex flex-row items-center gap-3 bg-white text-md py-0 ml-auto pr-0 shrink-0"
          >
            <notifications-popup icon-class="text-black" class="mr-0" />
            <Menu
              ref="userMenu"
              :model="userMenuItems"
              popup
              :pt="{
                root: 'min-w-46',
                item: { class: 'text-md' },
                label: { class: 'text-md' },
                itemLabel: (options) => ({
                  class: options.instance.item.isActive
                    ? 'text-primary-color'
                    : '',
                }),
                itemIcon: (options) => ({
                  class: options.instance.item.isActive
                    ? 'text-primary-color'
                    : '',
                }),
              }"
            />
            <button
              class="flex items-center gap-2 ml-2 mr-4 py-2 pl-4 pr-3 rounded-full font-semibold border-2 border-primary-color bg-white hover:bg-gray-100 text-base text-gray-800 transition"
              @click="toggleUserMenu"
            >
              <span class="max-w-40 truncate">{{ userName }}</span>
              <i class="pi pi-chevron-down"></i>
            </button>
          </div>
        </div>
      </div>
      <div v-if="showMenu" class="md:hidden">
        <div class="rounded-sm bg-white py-1 shadow-lg z-20 fixed w-full">
          <div
            v-for="(item, index) in menuItems"
            :key="index"
            :class="
              isUrl(item.path_name)
                ? 'text-primary-color bg-gray-200'
                : 'text-black hover:text-primary-color hover:bg-gray-200'
            "
          >
            <Link
              class="block py-2 px-4 w-full cursor-pointer"
              :href="item.path"
              role="navigation"
              :method="item.method"
              as="button"
              @click="showMenu = false"
            >
              {{ item.label }}
            </Link>
          </div>
        </div>
      </div>
      <!-- Overlay backdrop for mobile menu -->
      <div
        v-if="showMenu"
        class="fixed inset-0 bg-black/50 md:hidden z-10"
        @click="showMenu = false"
      ></div>
      <div class="grow">
        <div class="h-full" :style="pageBgStyle">
          <!-- Hidden for now -->
          <!-- <flash-messages /> -->
          <slot />
        </div>
      </div>
    </div>
  </minimal-layout>
</template>

<script>
import { Link } from '@inertiajs/vue3';
import MinimalLayout from '@/Layouts/Minimal.vue';
import FlashMessages from '@/Shared/FlashMessages.vue';
import Logo from '@/Shared/Logo.vue';
import MainMenuAdmin from '@/Shared/MainMenuAdmin.vue';
import NotificationsPopup from '@/Shared/NotificationsPopup.vue';
import Button from 'primevue/button';
import Menu from 'primevue/menu';
import bgImageUrl from '@/images/background.jpg';
import notificationsService from '@/services/notificationsService';

export default {
  components: {
    Link,
    MinimalLayout,
    FlashMessages,
    Logo,
    MainMenuAdmin,
    NotificationsPopup,
    Button,
    Menu,
  },
  data() {
    return {
      menuItems: [
        {
          label: 'Programe',
          icon: 'pi pi-book',
          path: this.$routes.user_courses(),
          path_name: ['', 'user_courses'],
          method: 'get',
        },
        {
          label: 'Profil',
          icon: 'pi pi-user',
          path: this.$routes.my_profile(),
          path_name: ['my_profile'],
          method: 'get',
        },
        {
          label: 'Logout',
          icon: 'pi pi-sign-out',
          path: this.$routes.destroy_user_session(),
          path_name: [],
          method: 'delete',
        },
      ],
      showMenu: false,
    };
  },
  computed: {
    userMenuItems() {
      return [
        {
          label: 'Programe',
          icon: 'pi pi-book',
          command: () => this.navigate(this.menuItems[0]),
          isActive: this.isUrl(this.menuItems[0].path_name),
        },
        {
          label: 'Profil',
          icon: 'pi pi-user',
          command: () => this.navigate(this.menuItems[1]),
          isActive: this.isUrl(this.menuItems[1].path_name),
        },
        {
          separator: true,
        },
        {
          label: 'Logout',
          icon: 'pi pi-sign-out',
          command: () => this.navigate(this.menuItems[2]),
        },
      ];
    },
    pageBgStyle() {
      return {
        backgroundImage: `url('${bgImageUrl}')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      };
    },
    userName() {
      const user = this.$page?.props?.auth?.user;
      if (!user) return '';
      return [user.first_name, user.last_name.charAt(0)]
        .filter(Boolean)
        .join(' ');
    },
  },
  mounted() {
    if (this.$page?.props?.auth?.user) {
      notificationsService.fetchUnreadCount();
      // notificationsService.startPolling();
    }
  },
  beforeUnmount() {
    notificationsService.stopPolling();
  },
  methods: {
    isUrl(urls) {
      let currentUrl = this.$page.url.substr(1);

      if (urls[0] === '' && currentUrl === '') {
        return true;
      }

      return urls.filter((url) => {
        if (url === '') return false;
        return currentUrl.startsWith(url);
      }).length;
    },
    toogleMenu() {
      this.showMenu = !this.showMenu;
    },
    toggleUserMenu(event) {
      this.$refs.userMenu.toggle(event);
    },
    navigate(item) {
      this.$inertia.visit(item.path, { method: item.method || 'get' });
    },
  },
};
</script>
